import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Container,
  Paper,
  LinearProgress,
  Button,
  IconButton,
  Chip,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fade,
  Zoom,
  Grow,
  Avatar,
  Divider,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  Verified as VerifiedIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  BarChart as ChartIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  MoreVert as MoreVertIcon,
  VerifiedUser as VerifiedUserIcon 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { DashboardStats } from '../types/index';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusChip from '../components/common/StatusChip';

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, hasRole, logout } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getDashboardStats();
      console.log('Dashboard stats received:', data); // Debug log
      setStats(data);
    } catch (err: any) {
      console.error('Dashboard stats error:', err); // Debug log
      if (err.status === 401 || err.response?.status === 401) {
        logout();
        navigate('/login');
        return; 
      }
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    trend?: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, subtitle, trend, onClick }) => (
    <Grow in={true} timeout={600}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 40px ${alpha(color, 0.2)}`,
          } : {},
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(color, 0.05)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(color, 0.1)}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 80,
            height: 80,
            bgcolor: alpha(color, 0.08),
            borderRadius: '0 0 0 80px',
          }}
        />
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(color, 0.12),
                color: color,
                mr: 2.5,
                boxShadow: `0 8px 16px ${alpha(color, 0.2)}`,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 0.5, fontSize: isMobile ? '2rem' : '2.5rem' }}>
                {value}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: '#4CAF50', mr: 0.5 }} />
              <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                {trend}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );

  const QuickAction: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    onClick: () => void;
    delay?: number;
  }> = ({ title, description, icon, color, onClick, delay = 0 }) => (
    <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Paper
        onClick={onClick}
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: 3,
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: `0 16px 32px ${alpha(color, 0.2)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: `1px solid ${alpha(color, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(color, 0.05)} 100%)`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: alpha(color, 0.05),
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.12),
              color: color,
              mb: 2,
              boxShadow: `0 8px 16px ${alpha(color, 0.15)}`,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {description}
          </Typography>
        </Box>
      </Paper>
    </Zoom>
  );

  if (loading) {
    return <LoadingSpinner message="Loading dashboard data..." fullScreen />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardStats}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Header Section - Enhanced */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography 
                variant={isMobile ? "h4" : "h3"} 
                sx={{ 
                  fontWeight: 900, 
                  mb: 1,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome, {user?.users_name || 'User'}! 👋
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchDashboardStats}
                sx={{ 
                  borderRadius: 2.5, 
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Stats Grid - Enhanced */}
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 2, mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {hasRole('DATA_ENTRY_OFFICER') ? "Today's Overview (My Records)" : "Today's Overview (All Records)"}
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {/* Card 4 – Total Entries Today */}
            <Grid size={{ xs: 12, sm: 6}}>
              <StatCard
                title="Today's Total Entries"
                value={stats?.today_total_entries ?? 0}
                icon={<ScheduleIcon sx={{ fontSize: 28 }} />}
                color="#2196F3"
                subtitle={
                  hasRole('DATA_ENTRY_OFFICER')
                    ? 'Your records added today'
                    : 'All records added today'
                }
              />
            </Grid>
            {/* Card 5 – Total Amount Today (Pending + Verified) */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <StatCard
                title="Today's Total Amount (JD)"
                value={(stats?.today_total_amount ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                icon={<MoneyIcon sx={{ fontSize: 28 }} />}
                color="#9C27B0"
                subtitle="Pending + Verified records"
              />
            </Grid>
            {/* Card 1 – Pending Today */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard
                title="Today's Pending"
                value={stats?.today_total_pending ?? 0}
                icon={<PendingIcon sx={{ fontSize: 28 }} />}
                color="#FF9800"
                subtitle="Current status: Pending"
              />
            </Grid>
            {/* Card 2 – Verified Today */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard
                title="Today's Verified"
                value={stats?.today_total_verified ?? 0}
                icon={<VerifiedIcon sx={{ fontSize: 28 }} />}
                color="#4CAF50"
                subtitle="Current status: Verified"
              />
            </Grid>
            {/* Card 3 – Deleted Today */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard
                title="Today's Deleted"
                value={stats?.today_total_deleted ?? 0}
                icon={<AssessmentIcon sx={{ fontSize: 28 }} />}
                color="#F44336"
                subtitle="Current status: Deleted"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Role-based Quick Actions */}
        {(hasRole('DATA_ENTRY_OFFICER') || hasRole('DATA_MANAGER') || hasRole('LEAD_MANAGER')) && (
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                width: 4, 
                height: 24, 
                bgcolor: 'primary.main', 
                borderRadius: 2, 
                mr: 2 
              }} />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Quick Actions
              </Typography>
            </Box>

            {/* Data Entry Officer Dashboard */}
            {hasRole('DATA_ENTRY_OFFICER') && (
              <Grid container spacing={3}>
                <Grid size = {{xs:12, md:4}}>
                  <QuickAction
                    title="New Entry"
                    description="Add new registration record with advanced form"
                    icon={<AddIcon />}
                    color="#2196F3"
                    onClick={() => navigate('/data-entry')}
                    delay={0}
                  />
                </Grid>
                <Grid size = {{xs:12, md:4}}>
                  <QuickAction
                    title="Edit / Delete Records"
                    description="Search and edit your pending records with filters"
                    icon={<SearchIcon />}
                    color="#4CAF50"
                    onClick={() => navigate('/manage-records')}
                    delay={100}
                  />
                </Grid>
                <Grid size = {{xs:12, md:4}}>
                  <QuickAction
                    title="View Register Reports"
                    description="Check your register report with insights"
                    icon={<ChartIcon />}
                    color="#FF9800"
                    onClick={() => navigate('/reports/register-report')}
                    delay={200}
                  />
                </Grid>
              </Grid>
            )}

            {/* Data Manager Dashboard */}
            {hasRole('DATA_MANAGER') && (
              <Grid container spacing={3}>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Manage Records"
                    description="Verify or delete any pending applications"
                    icon={<VerifiedIcon />}
                    color="#4CAF50"
                    onClick={() => navigate('/manage-records')}
                    delay={0}
                  />
                </Grid>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Daily Summary"
                    description="View daily summary of records with advanced filtering"
                    icon={<SearchIcon />}
                    color="#2196F3"
                    onClick={() => navigate('/reports/daily-summary')}
                    delay={100}
                  />
                </Grid>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Export Data"
                    description="Export records in Excel and PDF formats"
                    icon={<ExportIcon />}
                    color="#9C27B0"
                    onClick={() => navigate('/reports/register-report')}
                    delay={200}
                  />
                </Grid>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Audit Logs"
                    description="View system activity logs and security reports"
                    icon={<TimelineIcon />}
                    color="#FF9800"
                    onClick={() => navigate('/reports/audit-log')}
                    delay={300}
                  />
                </Grid>
              </Grid>
            )}

            {/* Lead Manager Dashboard */}
            {hasRole('LEAD_MANAGER') && (
              <Grid container spacing={3}>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Manage Records"
                    description="Verify or delete any pending applications"
                    icon={<VerifiedIcon />}
                    color="#4CAF50"
                    onClick={() => navigate('/manage-records')}
                    delay={0}
                  />
                </Grid>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Daily Summary"
                    description="View daily summary of records with advanced filtering"
                    icon={<SearchIcon />}
                    color="#2196F3"
                    onClick={() => navigate('/reports/daily-summary')}
                    delay={100}
                  />
                </Grid>
                <Grid size = {{xs:12, sm:6, md:3}}>
                  <QuickAction
                    title="Audit log"
                    description="Tracks each record activities and changes"
                    icon={<VerifiedUserIcon />}
                    color="#9C27B0"
                    onClick={() => navigate('/reports/audit-log')}
                    delay={200}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}
        {/* Footer Note 
        <Box sx={{ 
          mt: 4, 
          pt: 3, 
          borderTop: `1px solid ${alpha('#000', 0.05)}`,
          textAlign: 'center',
        }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • 
            Data refreshes every 5 minutes
          </Typography>
        </Box>
        */}
      </Container>
    </Fade>
  );
};

export default DashboardPage;